<script lang="ts" setup>
import Card from 'primevue/card'
import Button from 'primevue/button'
import InputSwitch from 'primevue/inputswitch'
import InputLabel from '@/components/inputs/InputLabel.vue'
import FormSection from '@/components/forms/FormSection.vue'
import FormInputText from '@/components/inputs/FormInputText.vue'
import CustomerAddressFields from '@/components/features/customers/CustomerAddressFields.vue'
import { type Customer} from '@/types/customer-types'
import router from '@/router'
import { onMounted, ref, watch } from 'vue'
import { computed } from 'vue'
import { Icon } from '@iconify/vue'
import { customerSchema } from '@/schemas/customerSchema'
import { convertErrorsToVeeValidateFormat } from '@/utils'
import { editCustomer, getCustomerById } from '@/api/customers'

const props = defineProps<{
  values: any
  addressFields: any[]
  type: 'add' | 'edit' | 'view'
  onSubmit: () => void
  setErrors: (errors: any) => void
  newAddress: (address: any) => void
  removeAddress: (index: number) => void
  showToast?: (type: 'success' | 'error', title: string, message: string) => void
}>()

const customerId = router.currentRoute.value?.params.id as string
const checked = ref<boolean | undefined>(props.values.isActive)
const customer = ref<Customer | null>(null)

const validateAddress = (): boolean => {
  const validation = customerSchema.pick({ addresses: true }).safeParse({
    addresses: props.values.addresses
  })
  if (!validation.success)
    props.setErrors(convertErrorsToVeeValidateFormat(validation.error.issues))

  return validation.success
}

const addNewAddress = () => {
  if (validateAddress()) props.newAddress({})
}

const isViewPage = computed<boolean>(() => props.type === 'view')
const isAddPage = computed<boolean>(() => props.type === 'add')
const showRemoveButton = computed<boolean>(() => props.addressFields.length > 1)
const showAddressSection = computed<boolean>(() => props.addressFields.length > 0)

const updateCustomerStatus = async () => {
  if(!customer.value) return;
  try {
    await editCustomer({...customer.value, isActive: checked.value }, customerId)
  }catch(err){
    console.error('Error updating customer status:', err)
  }
}

onMounted(async () => {
  try {
    customer.value = await getCustomerById(customerId)
    if (customer.value) {
      checked.value = customer.value.isActive
    }
  } catch (error) {
    console.error('Error fetching customer data:', error)
  }
})

watch(checked, () => {
  updateCustomerStatus()
})
</script>

<template>
  <form @submit.prevent="onSubmit" class="border-t border-neutral-200/50 !rounded-t-3xl">
    <Card>
      <template #content>
        <div class="">
          <FormSection title="Customer Information" :description="`${type === 'view'? '' : 'Enter the customer details'}`">
            <template #status v-if="!isAddPage">
              <div class="flex items-center gap-x-2 col-span-2">
                <InputLabel class="!mb-0">This customer is currently {{ customer?.isActive ? 'active' : 'inactive' }}</InputLabel>
                <InputSwitch v-model="checked" />
              </div>
            </template>

            <FormInputText
              :viewOnly="isViewPage"
              label="Business Name"
              inputName="businessName"
              id="businessName"
            />

            <FormInputText
              :viewOnly="isViewPage"
              label="Customer Account Reference"
              inputName="customerAccountReference"
              id="customerAccountReference"
            />

            <FormInputText
              :viewOnly="isViewPage"
              label="Sales Contact Name"
              inputName="salesContactName"
              id="salesContactName"
            />

            <FormInputText
              :viewOnly="isViewPage"
              label="Sales Contact Email"
              inputName="salesContactEmail"
              id="salesContactEmail"
            />

            <FormInputText
              :viewOnly="isViewPage"
              label="Sales Contact Landline Number"
              inputName="salesContactTelephone"
              id="salesContactTelephone"
            />

            <!-- <FormInputText
              :viewOnly="isViewPage"
              label="Sales Contact Mobile Number"
              inputName="salesContactMobile"
              id="salesContactMobile"
            /> -->

            <!-- this is commented because we need clarification from the customer -->
            <!-- <FormInputText
              :viewOnly="isViewPage"
              type="number"
              suffix="%"
              label="Discount for customer"
              inputName="discountPercentage"
              id="discountPercentage"
            /> -->

          </FormSection>
          <FormSection
            v-if="showAddressSection"
            title="Customer Address"
            description="Enter the customer address details"
          >
            <template v-for="(addressField, index) in addressFields" :key="addressField.key">
              <CustomerAddressFields
                :expandedAddressView="isAddPage || Object.keys(addressField.value).length === 0"
                :validateAddress="validateAddress"
                :formPath="`addresses[${index}]`"
                :showRemoveButton="showRemoveButton"
                :addressValues="values?.addresses[index]"
                :removeAddress="() => removeAddress(index)"
                :type="type"
              />
            </template>
          </FormSection>
        </div>
      </template>

      <template #footer>
        <Button
          v-ripple
          class="gap-x-2 w-full border-dashed py-4 mb-4"
          outlined
          type="button"
          @click="addNewAddress"
          v-if="!isViewPage"
        >
          <span>{{ showAddressSection ? 'Add Another Address' : 'Add Customer Address' }} </span>
          <Icon icon="ic:baseline-plus" class="text-xl" />
        </Button>
      </template>
    </Card>
  </form>
</template>
<style></style>